import { IAlertStatusType, IAppState, ILog, IMonitorStatusType, IShip, IShipStatus, IStatus, IStatusType } from "../../Types"
import { Ship } from "../../App/Ship"
import { Icons } from "../../Components/Icon"
import { UserAdviceLevel, UserAdviceRole } from "../../Components/UserAdvice/Params"
import { KeepAliveController, keepAliveOptedIn, setKeepAliveOptIn } from "../../App/KeepAlive"
import { FocusViewState } from "../FocusView"
import { UserAdvice, UserAdviceInstance, UserAdviceLocation, UserAdviceMonitor, UserAdviceState } from "./Advice"
import { OverlayViewDefinition } from "../OverlayView"
import { userAdviceAnchorMonitorOverlay } from "./AnchorMonitorView"
import { UserAdviceViewsData } from "./Context"
import { GPSPrecision, supportsGPSPrecision } from "../../App/GPSPrecision"

export class AnchorMonitorAdvice extends UserAdvice implements UserAdviceMonitor {
  readonly role = UserAdviceRole.monitor;
  active: boolean = false;
  enabled: boolean = false;
  monitor?: IStatus = undefined;
  alerts: IStatus[] = [];
  static level: number = 100;
  private keepAlive = new KeepAliveController();

  update(app: IAppState, state: FocusViewState, currentLog?: ILog, ship?: IShip) {
    this.active = false;
    this.enabled = false;
    this.monitor = undefined;
    this.alerts = [];
    if (!currentLog) {
      this.reconcileKeepAlive(app);
      return;
    }
    const monitorStatuses = UserAdvice.status(state.statuses, IStatusType.monitor, currentLog.dayId)
      .filter((s) => s.item == IMonitorStatusType.anchor)
      .toSorted((a, b) => b.id - a.id);
    const activeMonitor = monitorStatuses.find((status) => status.endLogId == undefined);
    if (activeMonitor) {
      this.monitor = activeMonitor;
      this.enabled = true;
    } else {
      const settings = ship ? Ship.shipSettings(ship).anchorWatchSettings : undefined;
      if (settings) {
        this.monitor = { data: JSON.stringify(settings) } as IStatus;
      } else if (monitorStatuses[0]) {
        this.monitor = monitorStatuses[0];
      }
    }
    // The watch dialog is always available when GPS precision is sufficient.
    // An already-running watch shows so it can be reviewed / stopped.
    this.active = this.enabled || supportsGPSPrecision(app, GPSPrecision.high);
    const shipStatus = UserAdvice.status(state.statuses, IStatusType.ship, currentLog.dayId).filter((s) => s.endLogId == undefined).toSorted((a, b) => b.id - a.id)[0];
    if (this.active) {
      const depthEnabled = this.enabled && shipStatus != undefined
        && (shipStatus.value == IShipStatus.anchor || shipStatus.value == IShipStatus.buoy);
      this.alerts = UserAdvice.status(state.statuses, IStatusType.alert, currentLog.dayId)
        .filter((s) => s.endLogId == undefined && (s.name == IAlertStatusType.anchorDrift || s.name == IAlertStatusType.lostPosition || (s.name == IAlertStatusType.depth && depthEnabled)));
    }
    this.reconcileKeepAlive(app);
  }

  // Hold the native background keep-alive on this (opt-in, non-primary) companion
  // while the anchor watch is actually running, so a backgrounded / locked phone keeps
  // receiving the LAN fan-out; release it when the watch stops or the device opts out.
  private reconcileKeepAlive(app: IAppState) {
    this.keepAlive.reconcile({
      watchActive: this.enabled,
      optedIn: keepAliveOptedIn(),
      isPrimary: app.primarySession(),
    });
  }

  // Toggle this device's background keep-alive opt-in from the anchor-watch UI, and
  // apply it immediately (rather than waiting for the next advice update).
  setKeepAlive(app: IAppState, optedIn: boolean) {
    setKeepAliveOptIn(optedIn);
    this.reconcileKeepAlive(app);
  }

  // The monitor-role contract (#916). `enabled` is this watch's own running state
  // and `active` is whether the dialog can be shown at all. The watch can be armed
  // before anchoring; its consumers remain status-gated until anchor/buoy.
  isActive(): boolean {
    return this.enabled;
  }

  hasProblem(app: IAppState): boolean {
    if (!this.enabled) {
      return false;
    }
    const nmea = app.status?.nmea;
    const navigation = nmea?.data.navigation;
    const position = nmea?.data.n2k?.position;
    const hasPosition = (Number.isFinite(navigation?.lat) && Number.isFinite(navigation?.lng))
      || (Number.isFinite(position?.latitude) && Number.isFinite(position?.longitude));
    // Automatic log writing is independent of position processing during Stopover.
    // The existing watchdog owns freshness, not lastReceived (any sensor traffic).
    return !hasPosition
      || this.alerts.some((status) => status.name == IAlertStatusType.lostPosition);
  }

  isAvailable(): boolean {
    return this.active;
  }

  activate(app: IAppState): Promise<void> {
    return this.setEnabled(app, true);
  }

  deactivate(app: IAppState): Promise<void> {
    return this.setEnabled(app, false);
  }

  // Through the dedicated lifecycle route, like the dialog's own submit: the
  // client never composes the monitor status write itself, so two devices toggling
  // the same watch cannot race into contradictory rows.
  private async setEnabled(app: IAppState, enabled: boolean): Promise<void> {
    await UserAdviceViewsData.anchorWatch(app.apiClient, { enabled });
  }

  getInstances(app: IAppState, location: UserAdviceLocation, currentLog?: ILog): UserAdviceInstance[] {
    // Keep settings in the ship toolbar, and show a notice-level reminder in the
    // current-log row while the watch is running. Alert conditions still surface
    // separately through AlertAdvice in their warning/alarm colours.
    //
    // The id stays location-scoped. FocusView keeps a single `selected` id across
    // every location (one FocusViewState per day), so two locations sharing an id
    // would have a click at one satisfy both instances' `state.selected == this.id`
    // check and open both dialogs at once.
    const visible = location == UserAdviceLocation.toolbar && this.active;
    if (!visible || !currentLog) {
      return [];
    }
    const t = app.getT("UserAdviceAnchorMonitorView");
    return [
      new UserAdviceInstance(`anchor-${location}`, AnchorMonitorAdvice.level, { icon: Icons.AdviceAnchor, level: UserAdviceLevel.notice, shortTitle: t("title_short"), title: t(this.enabled ? "title_active" : "title_inactive"), type: "anchor" }, this, 0, location),
    ];
  }

  overlay(_app: IAppState, advice: UserAdviceInstance, state: UserAdviceState): OverlayViewDefinition | undefined {
    return userAdviceAnchorMonitorOverlay({ advice, state });
  }
}
